import React, { createContext, useContext, useMemo, useReducer } from 'react';

import { type EmailAccount, type EmailMessage, type LocalFolder } from '~/common/types/mailbox';
import { type EmailTone, SettlementRecipientType } from '~/common/enums';
import { type InitialAiDraft } from '@/shared/components/2.0/email/aiDraft/types';
import { localStorageKeys } from '@/shared/utils/constants';
import { type Nullable } from '~/common/types';

export type MailboxState = {
  // Steer the auto-started AI draft (e.g. a Vera email proposal); absent means a plain draft.
  aiDraftPurpose: Nullable<string>;
  aiDraftTone: Nullable<EmailTone>;
  autoStartAiDraft: boolean;
  checkedEmailIds: string[];
  emailAccount: Nullable<EmailAccount>;
  folders: LocalFolder[];
  initialAiDraft: Nullable<InitialAiDraft>;
  isCompose: boolean;
  isEmailListVisible: boolean;
  isForward: boolean;
  isReplyAll: boolean;
  isRetry: boolean;
  recipientType: SettlementRecipientType;
  replyEmail: Nullable<EmailMessage>;
  selectedEmail: Nullable<EmailMessage>;
  selectedFolder: Nullable<string>;
  selectedFolderAttributes: string[];
  searchQuery: string;
  readFilter: string;
  targetMessageId: Nullable<string>;
};

type MailBoxContextType = {
  clearCompose: () => void;
  setCheckedEmailIds: (ids: string[]) => void;
  setEmailAccount: (emailAccount: EmailAccount, clear?: boolean) => void;
  setEmailRecipientType: (recipientType: SettlementRecipientType) => void;
  setSelectedFolder: (folderId: Nullable<string>) => void;
  setSelectedFolderAttributes: (attributes?: readonly string[] | null) => void;
  setSelectedEmail: (email: Nullable<EmailMessage>) => void;
  setFolders: (folders: LocalFolder[]) => void;
  state: MailboxState;
  updateState: (payload: Partial<MailboxState>) => void;
  setSearchQuery: (query: string) => void;
  setReadFilter: (filter: string) => void;
};

const MAILBOX_ACTIONS = {
  UPDATE_STATE: 'UPDATE_STATE',
};

const mailboxReducer = (
  state: MailboxState,
  action: { type: string; payload: Partial<MailboxState> },
) => {
  switch (action.type) {
    case MAILBOX_ACTIONS.UPDATE_STATE:
      return { ...state, ...action.payload };
    default:
      return { ...state };
  }
};

const INITIAL_STATE: MailboxState = {
  aiDraftPurpose: null,
  aiDraftTone: null,
  autoStartAiDraft: false,
  checkedEmailIds: [],
  emailAccount: null,
  folders: [],
  initialAiDraft: null,
  isCompose: false,
  isEmailListVisible: true,
  isForward: false,
  isReplyAll: false,
  isRetry: false,
  readFilter: 'all',
  recipientType: SettlementRecipientType.Everyone,
  replyEmail: null,
  searchQuery: '',
  selectedEmail: null,
  selectedFolder: null,
  selectedFolderAttributes: [],
  targetMessageId: null,
};

const MailboxContext = createContext({} as MailBoxContextType);

// `initialState` seeds a nested, isolated provider (e.g. one per case email
// compose instance); it is only read on mount.
export const MailboxStateProvider: React.FC<{
  children: React.ReactNode;
  initialState?: Partial<MailboxState>;
}> = ({ children, initialState }) => {
  const [state, dispatch] = useReducer(mailboxReducer, { ...INITIAL_STATE, ...initialState });

  const contextValue = useMemo(
    () => ({
      clearCompose: () =>
        dispatch({
          payload: {
            aiDraftPurpose: null,
            aiDraftTone: null,
            autoStartAiDraft: false,
            initialAiDraft: null,
            isCompose: false,
            isEmailListVisible: true,
            isForward: false,
            isReplyAll: false,
            isRetry: false,
            replyEmail: null,
            targetMessageId: null,
          },
          type: MAILBOX_ACTIONS.UPDATE_STATE,
        }),
      setCheckedEmailIds: (ids: string[]) =>
        dispatch({ payload: { checkedEmailIds: ids }, type: MAILBOX_ACTIONS.UPDATE_STATE }),
      setEmailAccount: (emailAccount: EmailAccount, clear = true) => {
        const payload = {
          emailAccount,
          selectedFolder: null,
          ...(clear && {
            aiDraftPurpose: null,
            aiDraftTone: null,
            autoStartAiDraft: false,
            initialAiDraft: null,
            replyEmail: null,
            selectedEmail: null,
            targetMessageId: null,
          }),
        };
        dispatch({ payload, type: MAILBOX_ACTIONS.UPDATE_STATE });

        localStorage.setItem(
          localStorageKeys.mailbox.SELECTED_EMAIL_LINK_ID,
          emailAccount?.linkId ?? '',
        );
      },
      setEmailRecipientType: (recipientType: SettlementRecipientType) =>
        dispatch({ payload: { recipientType }, type: MAILBOX_ACTIONS.UPDATE_STATE }),
      setFolders: (folders: LocalFolder[]) =>
        dispatch({
          payload: { folders: folders.sort((a, b) => a.name.localeCompare(b.name)) },
          type: MAILBOX_ACTIONS.UPDATE_STATE,
        }),
      setReadFilter: (filter: string) =>
        dispatch({ payload: { readFilter: filter }, type: MAILBOX_ACTIONS.UPDATE_STATE }),
      setSearchQuery: (payload: string) =>
        dispatch({ payload: { searchQuery: payload }, type: MAILBOX_ACTIONS.UPDATE_STATE }),
      setSelectedEmail: (email: Nullable<EmailMessage>) =>
        dispatch({ payload: { selectedEmail: email }, type: MAILBOX_ACTIONS.UPDATE_STATE }),
      setSelectedFolder: (folderId: Nullable<string>) =>
        dispatch({ payload: { selectedFolder: folderId }, type: MAILBOX_ACTIONS.UPDATE_STATE }),
      setSelectedFolderAttributes: (attributes?: readonly string[] | null) =>
        dispatch({
          payload: { selectedFolderAttributes: attributes?.length ? [...attributes] : [] },
          type: MAILBOX_ACTIONS.UPDATE_STATE,
        }),
      state,
      updateState: (payload: Partial<MailboxState>) =>
        dispatch({ payload, type: MAILBOX_ACTIONS.UPDATE_STATE }),
    }),
    [state],
  );

  return <MailboxContext.Provider value={contextValue}>{children}</MailboxContext.Provider>;
};

export const useMailboxContext = () => useContext(MailboxContext);
